import type {
  ModuleDefinition,
  SynergyAbilityModifier,
  SynergyDefinition,
  SynergyRequirement,
} from "./phase-three-types";

export interface SkillIdentityEdit {
  sourceId: string;
  id: string;
  name: string;
  tags: string[];
  presentationId?: string;
}

export interface SkillIdentityOverrideDocument {
  version: 1;
  removedIds: string[];
  edits: SkillIdentityEdit[];
}

const ID_PATTERN = /^[a-z][a-z0-9_]{1,63}$/;

export function applySkillIdentityOverrides(
  sourceModules: readonly ModuleDefinition[],
  document: SkillIdentityOverrideDocument,
): ModuleDefinition[] {
  const errors = validateSkillIdentityOverrideDocument(sourceModules, document);
  if (errors.length > 0) {
    throw new Error(
      `技能身份配置无效：\n${errors.map((message) => `- ${message}`).join("\n")}`,
    );
  }

  const removedIds = new Set(document.removedIds);
  const editsBySourceId = new Map(
    document.edits.map((edit) => [edit.sourceId, edit]),
  );
  const nameReplacements = new Map<string, string>();
  for (const edit of document.edits) {
    const source = sourceModules.find((module) => module.id === edit.sourceId);
    if (source && source.name !== edit.name) {
      nameReplacements.set(source.name, edit.name);
    }
  }

  return sourceModules.flatMap((module) => {
    if (removedIds.has(module.id)) {
      return [];
    }
    const edit = editsBySourceId.get(module.id);
    const evolutionTo = module.evolutionTo
      ? getSkillIdentityOverrideId(module.evolutionTo, document)
      : undefined;
    const evolutionFrom = module.evolutionFrom
      ? getSkillIdentityOverrideId(module.evolutionFrom, document)
      : undefined;
    return [
      {
        ...module,
        id: edit?.id ?? module.id,
        name: edit?.name ?? module.name,
        description: replaceSkillNames(module.description, nameReplacements),
        tags: edit ? [...edit.tags] : [...module.tags],
        companion:
          edit?.presentationId && module.companion
            ? {
                ...module.companion,
                presentationId: edit.presentationId,
              }
            : module.companion,
        evolutionTo,
        evolutionFrom,
      },
    ];
  });
}

export function applySkillIdentityOverridesToSynergies(
  synergies: readonly SynergyDefinition[],
  sourceModules: readonly ModuleDefinition[],
  document: SkillIdentityOverrideDocument,
): SynergyDefinition[] {
  const sourceById = new Map(sourceModules.map((module) => [module.id, module]));
  const nameReplacements = new Map<string, string>();
  for (const edit of document.edits) {
    const source = sourceById.get(edit.sourceId);
    if (source && source.name !== edit.name) {
      nameReplacements.set(source.name, edit.name);
    }
  }
  for (const removedId of document.removedIds) {
    const source = sourceById.get(removedId);
    const retainedBranch = document.edits.find(
      (edit) => edit.sourceId === `branch_${removedId}`,
    );
    if (source && retainedBranch) {
      nameReplacements.set(source.name, retainedBranch.name);
    }
  }

  return synergies.flatMap((synergy) => {
    const requirements: SynergyRequirement[] = [];
    let lostRequirement = false;
    for (const requirement of synergy.requirements) {
      if (requirement.kind === "module") {
        const moduleId = getSkillIdentityOverrideId(
          requirement.moduleId,
          document,
        );
        if (!moduleId) {
          lostRequirement = true;
        } else {
          requirements.push({ kind: "module", moduleId });
        }
        continue;
      }
      if (requirement.kind === "one_of") {
        const moduleIds = [
          ...new Set(
            requirement.moduleIds.flatMap((moduleId) => {
              const remapped = getSkillIdentityOverrideId(moduleId, document);
              return remapped ? [remapped] : [];
            }),
          ),
        ];
        if (moduleIds.length === 0) {
          lostRequirement = true;
        } else {
          requirements.push({ kind: "one_of", moduleIds });
        }
        continue;
      }
      requirements.push(requirement);
    }
    if (lostRequirement) {
      return [];
    }

    const abilityModifiers = synergy.abilityModifiers
      ?.map(
        (modifier): SynergyAbilityModifier => ({
          ...modifier,
          moduleIds: [
            ...new Set(
              modifier.moduleIds.flatMap((moduleId) => {
                const remapped = getSkillIdentityOverrideId(
                  moduleId,
                  document,
                );
                return remapped ? [remapped] : [];
              }),
            ),
          ],
        }),
      )
      .filter((modifier) => modifier.moduleIds.length > 0);

    return [
      {
        ...synergy,
        name: replaceSkillNames(synergy.name, nameReplacements),
        description: replaceSkillNames(
          synergy.description,
          nameReplacements,
        ),
        requirements,
        abilityModifiers,
      },
    ];
  });
}

export function getSkillIdentityOverrideId(
  sourceId: string,
  document: SkillIdentityOverrideDocument,
): string | undefined {
  if (document.removedIds.includes(sourceId)) {
    return undefined;
  }
  return (
    document.edits.find((edit) => edit.sourceId === sourceId)?.id ?? sourceId
  );
}

export function getSkillIdentitySourceId(
  moduleId: string,
  document: SkillIdentityOverrideDocument,
): string {
  return (
    document.edits.find((edit) => edit.id === moduleId)?.sourceId ?? moduleId
  );
}

export function validateSkillIdentityOverrideDocument(
  sourceModules: readonly ModuleDefinition[],
  document: SkillIdentityOverrideDocument,
): string[] {
  const errors: string[] = [];
  if (document.version !== 1) {
    errors.push("技能身份配置 version 必须为 1。");
  }

  const sourceById = new Map(sourceModules.map((module) => [module.id, module]));
  const removedIds = new Set<string>();
  for (const id of document.removedIds) {
    if (!ID_PATTERN.test(id)) {
      errors.push(`待删除技能ID无效：${id}。`);
    }
    if (!sourceById.has(id)) {
      errors.push(`待删除技能不存在：${id}。`);
    }
    if (removedIds.has(id)) {
      errors.push(`待删除技能ID重复：${id}。`);
    }
    removedIds.add(id);
  }

  const editedSourceIds = new Set<string>();
  for (const edit of document.edits) {
    const source = sourceById.get(edit.sourceId);
    if (!source) {
      errors.push(`待编辑技能不存在：${edit.sourceId}。`);
    }
    if (removedIds.has(edit.sourceId)) {
      errors.push(`技能不能同时删除和编辑：${edit.sourceId}。`);
    }
    if (editedSourceIds.has(edit.sourceId)) {
      errors.push(`待编辑技能ID重复：${edit.sourceId}。`);
    }
    editedSourceIds.add(edit.sourceId);
    if (!ID_PATTERN.test(edit.id)) {
      errors.push(`技能新ID无效：${edit.id}。`);
    }
    if (edit.name.trim().length === 0) {
      errors.push(`技能 ${edit.sourceId} 的新名称不能为空。`);
    }
    if (
      edit.tags.length === 0 ||
      edit.tags.some((tag) => tag.trim().length === 0)
    ) {
      errors.push(`技能 ${edit.sourceId} 的标签必须是非空字符串。`);
    }
    if (new Set(edit.tags).size !== edit.tags.length) {
      errors.push(`技能 ${edit.sourceId} 的标签存在重复。`);
    }
    if (edit.presentationId && !source?.companion) {
      errors.push(`技能 ${edit.sourceId} 没有伙伴，不能设置表现接口。`);
    }
  }

  const resultIds = new Set<string>();
  const resultNames = new Set<string>();
  for (const module of sourceModules) {
    if (removedIds.has(module.id)) {
      continue;
    }
    const edit = document.edits.find(
      (candidate) => candidate.sourceId === module.id,
    );
    const resultId = edit?.id ?? module.id;
    const resultName = edit?.name ?? module.name;
    if (resultIds.has(resultId)) {
      errors.push(`应用技能身份配置后ID重复：${resultId}。`);
    }
    if (resultNames.has(resultName)) {
      errors.push(`应用技能身份配置后名称重复：${resultName}。`);
    }
    resultIds.add(resultId);
    resultNames.add(resultName);
  }

  for (const module of sourceModules) {
    if (removedIds.has(module.id)) {
      continue;
    }
    for (const relatedId of [module.evolutionTo, module.evolutionFrom]) {
      if (
        relatedId &&
        !getSkillIdentityOverrideId(relatedId, document)
      ) {
        errors.push(`不能只删除固定升格关系的一侧：${module.id} -> ${relatedId}。`);
      }
    }
  }

  return [...new Set(errors)];
}

function replaceSkillNames(
  value: string,
  replacements: ReadonlyMap<string, string>,
): string {
  let result = value;
  for (const [source, target] of replacements) {
    result = result.split(source).join(target);
  }
  return result;
}
